import { cn } from "@/lib/utils"

export interface TrustBreakdownLine {
  key: string
  label: string
  points: number
  negative: boolean
}

export interface TrustScore {
  tier: "new" | "verified" | "established" | "trusted"
  label: string
  breakdown: TrustBreakdownLine[]
  computed_at: string | null
}

/**
 * The Broker Score tier (INCO-336), as a word.
 *
 * Never a number. "Established" is a claim a desk can live with; "72" invites
 * a comparison the evidence cannot support. A New desk carries nothing at
 * all, on the same rule as the seal: absence is the signal, and a grey "New"
 * chip is a claim about someone.
 *
 * Deliberately not brass — brass means attested, and this is a conclusion,
 * not an attestation. Ink on a quiet wash, getting firmer with the tier.
 */
export default function TrustTierBadge({
  trust,
  className = "",
}: {
  trust?: TrustScore | null
  className?: string
}) {
  if (!trust || trust.tier === "new") return null

  const tone =
    trust.tier === "trusted"
      ? "bg-foreground text-background border-foreground"
      : trust.tier === "established"
        ? "bg-muted text-foreground border-border"
        : "text-muted-foreground border-border"

  return (
    <span
      data-trust-tier={trust.tier}
      title={`${trust.label} — Broker Score`}
      className={cn(
        "inline-flex h-[18px] shrink-0 items-center rounded-sm border px-1.5 text-xs font-semibold",
        tone,
        className
      )}
    >
      {trust.label}
    </span>
  )
}

/**
 * Why the tier is what it is. Each line is a reason a counterparty can read
 * and a desk can act on; the lines name counts and commodities, never
 * counterparties. Renders nothing with an empty breakdown — which is what a
 * stranger gets, and what a New desk has.
 */
export function TrustBreakdown({ trust }: { trust?: TrustScore | null }) {
  if (!trust || trust.breakdown.length === 0) return null

  return (
    <section className="border-border rounded-sm border" data-trust-breakdown>
      <header className="border-border flex items-center gap-2 border-b px-3 py-2">
        <span className="text-muted-foreground flex-1 text-xs font-semibold">
          Broker Score
        </span>
        <TrustTierBadge trust={trust} />
      </header>
      <ul className="divide-border divide-y">
        {trust.breakdown.map((line) => (
          <li key={line.key} className="flex items-center gap-3 px-3 py-1.5 text-sm">
            <span className={cn("flex-1", line.negative ? "text-warning" : "")}>{line.label}</span>
            <span
              className={cn(
                "text-xs",
                line.negative ? "text-warning" : "text-muted-foreground"
              )}
            >
              {line.points > 0 ? `+${line.points}` : line.points}
            </span>
          </li>
        ))}
      </ul>
      <p className="text-muted-foreground border-border border-t px-3 py-1.5 text-xs">
        A tier, not a score: the points order desks within a tier and explain it. Each tier also
        needs the evidence behind it — verification, confirmed closes — not points alone.
      </p>
    </section>
  )
}
